<template>
  <div
    class="a-button-animate"
    :style="{ width: `${width}px`, height: `${height}px`, 'cursor': scale ? 'pointer' : 'default' }"
  >
    <div v-if="transform" class="for-mobile text">
      <svg width="310" height="140" viewBox="0 0 400 200">
        <defs>
          <path id="smilePath1" d="M 50,100 Q 200,130 350,100" fill="none" />
          <path
            v-if="remain"
            id="smilePath2"
            d="M 50,120 Q 200,140 350,120"
            fill="none"
          />
          <path id="smilePath3" d="M 50,100 Q 200,120 350,100" fill="none" />
        </defs>

        <text
          font-size="34px"
          font-weight="900"
          letter-spacing="1"
          fill="white"
          font-family="ALS Gorizont Variable"
          text-anchor="middle"
        >
          <textPath :href="remain ? '#smilePath3' : '#smilePath1'" startOffset="50%">{{ text }}</textPath>
        </text>

        <text
          v-if="remain"
          font-size="14px"
          font-weight="700"
          fill="white"
          font-family="ALS Gorizont Variable"
          text-anchor="middle"
        >
          <textPath href="#smilePath2" startOffset="50%">ОСТАЛОСЬ {{ remain || 0 }}%</textPath>
        </text>
      </svg>
    </div>

    <div v-else class="text">
      <AText
        tag="span"
        :size-lg="{ size: 24, line: 20 }"
        :size-sm="{ size: 20, line: 18 }"
      >
        {{ text }}
      </AText>

      <AText
        v-if="remain"
        tag="span"
        :size-lg="{ size: 12, line: 12 }"
        :size-sm="{ size: 12, line: 18 }"
      >
        Осталось {{ remain || 0 }}%
      </AText>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import AText from '~/components/atoms/text/a-text.vue';

interface Props {
  width?: number,
  height?: number,
  color?: 'pink' | 'white' | 'purple',
  scale?: boolean,
  transform?: boolean,
  remain?: number,
  isBuy?: boolean,
  text?: string,
}

const props = withDefaults(defineProps<Props>(), {
  width: 310,
  height: 75,
  color: 'pink',
  scale: true,
  transform: false,
  remain: 0,
  text: 'БИЛЕТЫ',
  isBuy: false,
});

const marginTopFactor = computed(() => {
  return props.remain ? 10 : 0;
});

const colorBg = computed(() => {
  switch (props.color) {
    case 'pink':
      return 'linear-gradient(to right, rgba(255, 182, 193, 0) 0%, var(--pink-color) 50%, rgba(255, 182, 193, 0) 100%)';
    case 'white':
      return 'linear-gradient(to right, rgba(255, 182, 193, 0) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 182, 193, 0) 100%)';
    case 'purple':
      return 'linear-gradient(to right, rgba(255, 182, 193, 0) 0%, rgb(120, 12, 255, 0.3) 50%, rgba(255, 182, 193, 0) 100%)';
    default:
      return 'linear-gradient(to right, rgba(255, 182, 193, 0) 0%, var(--pink-color) 50%, rgba(255, 182, 193, 0) 100%)';
  }
});

const colorAnimate = computed(() => {
  switch (props.color) {
    case 'pink':
      return 'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgb(255, 32, 150) 50%, rgba(255, 255, 255, 0) 100%)';
    case 'white':
      return 'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0) 100%)';
    case 'purple':
      return 'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgb(120, 12, 255, 0.4) 50%, rgba(255, 255, 255, 0) 100%)';
    default:
      return 'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgb(255, 32, 150) 50%, rgba(255, 255, 255, 0) 100%)';
  }
});

const scaleHover = computed(() => {
  if (props.scale) {
    return 'scale(1.05)';
  }
  return 'scale(1)';
});

const scaleActive = computed(() => {
  if (props.scale) {
    return 'scale(0.95)';
  }
  return 'scale(1)';
});

const path = computed(() => {
  if (props.transform) {
    return `path('M1,10 Q155,${props.height / 2.5} 310,10 L310,66 Q155,${props.height} 0,65 Z')`;
  }

  return '';
});
</script>

<style scoped lang="postcss">
.a-button-animate {
  background: v-bind(colorBg);
  position: relative;
  text-align: center;
  overflow: hidden;
  transition: transform 0.3s ease;
  clip-path: v-bind(path);
  color: var(--white-color);

  display: flex;
  flex-direction: column;
  justify-content: center;

  &:hover {
    transform: v-bind(scaleHover);
  }

  &:active {
    transform: v-bind(scaleActive);
  }
}

@media screen and (min-width: 767px) {
  .a-button-animate::after {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    width: 70%;
    height: 100%;
    background: v-bind(colorAnimate);
    animation: shimmer 2s infinite ease-in-out;
    transform: translateX(-50%);
  }
}

:deep(.text) {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

@keyframes shimmer {
  0% {
    opacity: 0.1;
    left: 25%;
  }
  50% {
    opacity: 1;
  }
  100% {
    left: 75%;
    opacity: 0.1;
  }
}
</style>
